import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent } from "../../components/ui/Card";
import { Sparkles, ArrowLeft, Shield, Eye, Lock } from "lucide-react";

const Privacy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-900 via-[#0A051E] to-emerald-900 p-8 overflow-hidden">
      <div className="max-w-4xl mx-auto relative">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-[600px] h-[600px] bg-emerald-900/20 rounded-full blur-3xl animate-pulse-slow" />
        </div>

        {/* Back Button */}
        <Link
          to="/home"
          className="mb-6 inline-flex items-center gap-2 text-white/60 hover:text-white transition-all duration-300 text-sm font-medium group relative z-10"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform duration-300" />
          <span>Back</span>
        </Link>

        <Card className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl hover:border-emerald-600/50 transition-all relative z-10">
          <CardContent className="p-8">
            <div className="space-y-8">
              {/* Header */}
              <div className="flex items-center justify-center gap-3 mb-8">
                <Shield className="w-8 h-8 text-emerald-300 animate-pulse" />
                <h1 className="text-3xl font-bold text-emerald-300">Privacy Policy</h1>
                <Shield className="w-8 h-8 text-emerald-300 animate-pulse" />
              </div>
              
              <section className="space-y-4">
                <p className="text-sm text-gray-400">Last Updated: June 12, 2025</p>
                <div className="bg-emerald-900/20 border border-emerald-500/30 p-4 rounded-lg">
                  <p className="text-emerald-200 font-medium">
                    <strong>ENTERTAINMENT & RESEARCH PLATFORM:</strong> PickStar is a sports analytics and intelligence platform designed exclusively for entertainment and research purposes. We are committed to protecting your privacy while providing engaging sports content for research enjoyment.
                  </p>
                </div>
                <p className="text-gray-200">
                  At PickStar ("we," "us," or "our"), we are committed to respecting your privacy and protecting your data. This Privacy Policy explains how we collect, use, and safeguard information when you access or use our sports analytics and entertainment services, website, and related platforms (collectively, the "Services").
                </p>
                <p className="text-gray-200">
                  By using PickStar, you acknowledge that you have read and agree to this Policy. If you do not agree with our practices, please discontinue use of our Services.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-xl font-semibold text-emerald-200 bg-clip-text text-transparent bg-gradient-to-r from-emerald-300 to-emerald-500">
                  1. Information We Collect
                </h2>
                <div className="bg-blue-900/20 border border-blue-500/30 p-4 rounded-lg">
                  <p className="text-blue-200 font-medium">
                    <strong>ENTERTAINMENT USE ONLY:</strong> We collect information solely to enhance your entertainment and research experience with sports analytics content.
                  </p>
                </div>
                <p className="text-gray-200">
                  We collect limited information to provide and improve our entertainment and research Services:
                </p>

                <h3 className="text-lg font-medium text-emerald-300">1.1. Account Information</h3>
                <ul className="list-disc pl-6 space-y-2 text-gray-300">
                  <li>Email address (through Google authentication for entertainment platform access)</li>
                  <li>Session identifiers for security and bot detection on our entertainment platform</li>
                </ul>

                <h3 className="text-lg font-medium text-emerald-300">1.2. Usage Information</h3>
                <ul className="list-disc pl-6 space-y-2 text-gray-300">
                  <li>Conversation data: Your interactions with our AI for sports entertainment and education</li>
                  <li>Usage metrics: When sports analytics content is liked, copied, or favorited for entertainment</li>
                  <li>Standard analytics data: Pages visited, time spent enjoying our entertainment content, etc.</li>
                </ul>
                
                <p className="text-gray-200">
                  By using our entertainment Services, you agree to our collection and use of this data, including your conversations about sports analytics, to improve our research and entertainment product offerings.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-xl font-semibold text-emerald-200 bg-clip-text text-transparent bg-gradient-to-r from-emerald-300 to-emerald-500">
                  2. How We Use Your Information
                </h2>
                <p className="text-gray-200">
                  We use the collected information exclusively for entertainment and research purposes:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-300">
                  <li>Providing and personalizing our sports entertainment and research Services</li>
                  <li>Improving our entertainment product based on usage patterns and user engagement with sports content</li>
                  <li>Security and fraud prevention for our entertainment platform</li>
                  <li>Analytics and trend analysis to enhance entertainment value</li>
                  <li>Research and development of AI technologies for sports entertainment and education</li>
                </ul>
                <div className="bg-amber-900/20 border border-amber-500/30 p-4 rounded-lg">
                  <p className="text-amber-200 font-medium">
                    <strong>ENTERTAINMENT FOCUS:</strong> Your conversation data about sports analytics is used to understand how users engage with our entertainment content and to improve our research sports intelligence platform. Currently, we retain this data permanently, though we plan to implement a 24-month retention policy in the future.
                  </p>
                </div>
              </section>

              <section className="space-y-4">
                <h2 className="text-xl font-semibold text-emerald-200 bg-clip-text text-transparent bg-gradient-to-r from-emerald-300 to-emerald-500">
                  3. Data Security
                </h2>
                <p className="text-gray-200">
                  We implement reasonable security measures to protect your information from unauthorized access or disclosure while you enjoy our entertainment platform. However, no method of transmission over the internet or electronic storage is 100% secure. While we strive to protect your data on our entertainment services, we cannot guarantee absolute security.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-xl font-semibold text-emerald-200 bg-clip-text text-transparent bg-gradient-to-r from-emerald-300 to-emerald-500">
                  4. Data Sharing and Disclosure
                </h2>
                <p className="text-gray-200">
                  We share your information under the following circumstances for our entertainment platform operations:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-300">
                  <li>
                    <strong>Third-Party Service Providers:</strong> We use services from Google (Firebase, Analytics), OpenAI, and occasionally Stripe for standard entertainment platform operations. These providers access only the data necessary for their specific functions in supporting our sports entertainment services.
                  </li>
                  <li>
                    <strong>Anonymized Entertainment Data:</strong> We may share or sell de-identified conversation data and aggregated reports about sports entertainment engagement that do not contain personal information.
                  </li>
                  <li>
                    <strong>Legal Requirements:</strong> We may disclose information if required by law or to protect our rights or the safety of others using our entertainment platform.
                  </li>
                </ul>
                <p className="text-gray-200">
                  We do not sell personal information (such as email addresses) to third parties for marketing purposes beyond our entertainment and research focus.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-xl font-semibold text-emerald-200 bg-clip-text text-transparent bg-gradient-to-r from-emerald-300 to-emerald-500">
                  5. Your Privacy Rights
                </h2>
                <p className="text-gray-200">
                  Depending on your jurisdiction, you may have certain rights regarding your personal data collected through our entertainment services. You can request:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-300">
                  <li>Access to your personal data from our entertainment platform</li>
                  <li>Correction of inaccurate data in your entertainment account</li>
                  <li>Deletion of your data (subject to our legitimate business needs and legal obligations for entertainment service provision)</li>
                </ul>
                <p className="text-gray-200">
                  To exercise these rights regarding your entertainment platform data, please contact us at <span className="text-emerald-300">support@pickstar.ai</span>. We may need to verify your identity before processing your request.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-xl font-semibold text-emerald-200 bg-clip-text text-transparent bg-gradient-to-r from-emerald-300 to-emerald-500">
                  6. Children's Privacy
                </h2>
                <div className="bg-red-900/20 border border-red-500/30 p-4 rounded-lg">
                  <p className="text-red-200 font-medium">
                    <strong>AGE RESTRICTION:</strong> Our sports entertainment and research Services are not intended for individuals under 18 years of age. We do not knowingly collect personal information from children through our entertainment platform.
                  </p>
                </div>
                <p className="text-gray-200">
                  If we learn that we have collected personal information from a child through our sports entertainment services, we will take steps to delete that information promptly.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-xl font-semibold text-emerald-200 bg-clip-text text-transparent bg-gradient-to-r from-emerald-300 to-emerald-500">
                  7. Entertainment Platform Disclaimer
                </h2>
                <div className="bg-purple-900/20 border border-purple-500/30 p-4 rounded-lg">
                  <p className="text-purple-200 font-medium">
                    <strong>ENTERTAINMENT PURPOSE REMINDER:</strong> All data collection and processing is performed solely to enhance your entertainment, research, and research experience with sports analytics content. Our platform is designed exclusively for sports enthusiasts seeking analytical entertainment, research, and research insights.
                  </p>
                </div>
              </section>

              <section className="space-y-4">
                <h2 className="text-xl font-semibold text-emerald-200 bg-clip-text text-transparent bg-gradient-to-r from-emerald-300 to-emerald-500">
                  8. Changes to This Policy
                </h2>
                <p className="text-gray-200">
                  We may update this Privacy Policy from time to time to better serve our entertainment platform users. We will notify you of significant changes by posting the new policy on our website or by other means. Your continued use of our sports entertainment Services after such modifications constitutes your acceptance of the updated policy.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-xl font-semibold text-emerald-200 bg-clip-text text-transparent bg-gradient-to-r from-emerald-300 to-emerald-500">
                  9. Contact Us
                </h2>
                <p className="text-gray-200">
                  If you have any questions about this Privacy Policy or our data practices for our sports entertainment platform, please contact us at:
                </p>
                <p className="text-emerald-300">Email: privacy@pickstar.ai</p>
                
                <div className="mt-6 p-4 bg-gradient-to-r from-emerald-500/10 to-violet-500/10 rounded-xl border border-emerald-500/20">
                  <p className="text-white/70 text-sm">
                    <strong>Final Note:</strong> PickStar operates as a sports intelligence and analytics platform designed exclusively for entertainment and research purposes. All privacy practices are aligned with providing engaging, research sports content for your enjoyment and learning.
                  </p>
                </div>
              </section>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Privacy;